import React from 'react';
import { Button, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FJ111 } from '../../../services/j201Api';
import { fmt } from '../helpers';

interface J201DetailTableProps {
  details: Partial<FJ111>[];
  isEditing: boolean;
  isCharged: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onDetailChange: (idx: number, key: keyof FJ111, value: FJ111[keyof FJ111]) => void;
}

const J201DetailTable: React.FC<J201DetailTableProps> = React.memo(({
  details, isEditing, isCharged, onAdd, onDelete, onDetailChange,
}) => {
  const priceDisabled = !isEditing || !isCharged;

  return (
    <div className="j201-tab2">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          申請明細 ({details.length} 筆)
          <span style={{ marginLeft: 16, fontWeight: 400, fontSize: 13, color: '#666' }}>總項數: {details.length}</span>
        </span>
        {isEditing && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onAdd}>
            新增明細
          </Button>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 36 }} data-contract-field="fj111_02">#</th>
              <th style={{ width: 80 }} data-contract-field="fj111_10">產品代號1</th>
              <th style={{ width: 80 }} data-contract-field="fj111_11">產品代號2</th>
              <th style={{ width: 60 }} data-contract-field="fj111_12">色號</th>
              <th style={{ width: 80 }} data-contract-field="fj111_121">中文色名</th>
              <th style={{ width: 80 }} data-contract-field="fj111_122">英文色名</th>
              <th style={{ width: 70 }} data-contract-field="fj111_12a">CBNO</th>
              <th style={{ width: 80 }} data-contract-field="fj111_13">申請數量</th>
              <th style={{ width: 50 }} data-contract-field="fj111_14">單位</th>
              <th style={{ width: 90 }} data-contract-field="fj111_15">未稅售價</th>
              <th style={{ width: 90 }} data-contract-field="fj111_151">含稅售價</th>
              <th style={{ width: 90 }} data-contract-field="fj111_16">未稅金額</th>
              <th style={{ width: 90 }} data-contract-field="fj111_161">含稅金額</th>
              <th style={{ width: 120 }} data-contract-field="fj111_17">產品說明</th>
              {isEditing && <th style={{ width: 46 }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 15 : 14} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <input
                    type="text" maxLength={6} disabled={!isEditing}
                    value={d.fj111_10 || ''}
                    onChange={e => onDetailChange(idx, 'fj111_10', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={6} disabled={!isEditing}
                    value={d.fj111_11 || ''}
                    onChange={e => onDetailChange(idx, 'fj111_11', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={10} disabled={!isEditing}
                    value={d.fj111_12 || ''}
                    onChange={e => onDetailChange(idx, 'fj111_12', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input type="text" readOnly value={d.fj111_121 || ''} className="field-readonly" style={{ width: '100%' }} />
                </td>
                <td>
                  <input type="text" readOnly value={d.fj111_122 || ''} className="field-readonly" style={{ width: '100%' }} />
                </td>
                <td>
                  <input
                    type="text" maxLength={10} disabled={!isEditing}
                    value={d.fj111_12a || ''}
                    onChange={e => onDetailChange(idx, 'fj111_12a', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.01" disabled={!isEditing}
                    value={d.fj111_13 ?? 0}
                    onChange={e => onDetailChange(idx, 'fj111_13', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={4} disabled={!isEditing}
                    value={d.fj111_14 || ''}
                    onChange={e => onDetailChange(idx, 'fj111_14', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.001" disabled={priceDisabled}
                    value={d.fj111_15 ?? 0}
                    onChange={e => onDetailChange(idx, 'fj111_15', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td>
                  <input
                    type="number" step="0.001" disabled={priceDisabled}
                    value={d.fj111_151 ?? 0}
                    onChange={e => onDetailChange(idx, 'fj111_151', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td className="field-amount">{fmt(d.fj111_16)}</td>
                <td className="field-amount">{fmt(d.fj111_161)}</td>
                <td>
                  <input
                    type="text" disabled={!isEditing}
                    value={d.fj111_17 || ''}
                    onChange={e => onDetailChange(idx, 'fj111_17', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                {isEditing && (
                  <td>
                    <Popconfirm title="確定刪除此明細?" onConfirm={() => onDelete(idx)}>
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

J201DetailTable.displayName = 'J201DetailTable';
export default J201DetailTable;
